import { CMS_APP_ID, itemTypeConfigMap, MembersCustomPageManagedByWixAppId, staticPageUtils, tpaPageIdToItemTypeConfig, WIX_CODE_APP_ID } from '@wix/a11y-wizard-common';
import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { SecurityType } from '@wix/ambassador-document-v1-page/types';
import { getSiteStructure } from '@wix/ambassador-document-v1-site-structure/http';
import { getSeoUserConfig } from '@wix/ambassador-promote-seo-v1-seo-user-config/http';
import { MEMBERS_AREA, MEMBERS_AREA_V2 } from '@wix/app-definition-ids';
import { createSiteStructureGraph, getSlugWithHierarchy, isPageExists } from '@wix/seo-page-hierarchy-library';
import { fetchSiteStructure } from '../api/fetch-site-structure';
import { fetchPagesData } from '../api/new-infra-fetchers/fetch-pages-data';
import { fetchRoutersData } from '../api/new-infra-fetchers/fetch-routers-data';
import { SITE_LEVEL_PAGE_ID } from '../consts';
const PORTFOLIO_APP_ID = itemTypeConfigMap[ITEM_TYPES.PORTFOLIO_COLLECTIONS].appDefId;
const getSiteStructureGraph = async httpClient => {
  const [{
    data: getSiteStructureResponse
  }, {
    data: {
      seoUserConfig
    }
  }] = await Promise.all([httpClient.request(getSiteStructure({})), httpClient.request(getSeoUserConfig({}))]);
  return seoUserConfig != null && seoUserConfig.shouldFlattenUrlHierarchy ? undefined : createSiteStructureGraph(getSiteStructureResponse);
};
export const mapPageIdToPageData = async (a11yWizardAdapter, i18n, isExperimentEnabled, httpClient, errorHandler) => {
  const {
    nonGeneralPageIdPagesGroupMap
  } = (a11yWizardAdapter.getAllPagesData == null ? void 0 : a11yWizardAdapter.getAllPagesData()) || [];
  const [pagesDataRaw, routersData, siteStructureGraph] = await Promise.all([fetchPagesData(httpClient, errorHandler, a11yWizardAdapter), fetchRoutersData(httpClient, errorHandler), getSiteStructureGraph(httpClient)]);
  const pagesData = filterOutCMSPages(pagesDataRaw, routersData);
  const portfolioPages = getPortfolioPagesFromRouters(routersData);
  // todo: someday when portdolio add tpa data to DM, it can be deleted
  if (portfolioPages) {
    pagesData.forEach(page => {
      const isPorfolioPage = portfolioPages.find(portfolioPage => portfolioPage.id === page.id);
      if (isPorfolioPage) {
        page.staticPage = {
          ...page.staticPage,
          tpa: {
            pageIntent: isPorfolioPage.tpaPageId,
            wixAppId: PORTFOLIO_APP_ID
          }
        };
      }
    });
  }
  const staticPages = filterStaticPages(pagesData, isExperimentEnabled);
  const staticPageDataEntries = staticPages.map(page => [page.id ?? '', buildStaticPageData(page, nonGeneralPageIdPagesGroupMap, i18n, siteStructureGraph)]);
  const templatePages = filterTemplatePages(pagesData, isExperimentEnabled);
  const templatePageDataEntries = (await Promise.all(templatePages.map(async _ref => {
    let {
      page,
      itemTypeConfig
    } = _ref;
    return [page.id ?? '', await buildTemplatePageData(page, itemTypeConfig, nonGeneralPageIdPagesGroupMap, i18n, httpClient, errorHandler)];
  }))).filter(_ref2 => {
    let [_, {
      innerRoutes
    }] = _ref2;
    return ((innerRoutes == null ? void 0 : innerRoutes.length) ?? 0) > 0;
  });
  return new Map([[SITE_LEVEL_PAGE_ID, {
    dividerTitle: i18n.t('accessibility-wizard.rule-group.label.site-level'),
    itemType: 'SITE_LEVEL',
    title: i18n.t('accessibility-wizard.rule-group.label.site-level'),
    tpaPage: false,
    urlPath: ''
  }], ...staticPageDataEntries, ...templatePageDataEntries]);
};
function filterStaticPages(pages, isExperimentEnabled) {
  return pages.filter(page => {
    if (page.managedByWixAppId === MEMBERS_AREA || page.managedByWixAppId === MEMBERS_AREA_V2) {
      return false;
    }
    if (page.managedByWixAppId === MembersCustomPageManagedByWixAppId.SignUp || page.managedByWixAppId === MembersCustomPageManagedByWixAppId.SignIn) {
      return true;
    }
    const {
      pageSecurity,
      appDefinitionId,
      managingAppDefId,
      tpaPageId
    } = getAttributesFromPage(page);
    const isProtected = pageSecurity ? pageSecurity.securityType !== SecurityType.NONE : false;
    const itemTypeConfigs = staticPageUtils.getItemTypeConfigsByAppDefinitionId(appDefinitionId ?? managingAppDefId);
    return !isProtected && (itemTypeConfigs == null ? void 0 : itemTypeConfigs.some(_ref3 => {
      let {
        itemType
      } = _ref3;
      return staticPageUtils.shouldScanPage(itemType, tpaPageId, isExperimentEnabled);
    }));
  });
}
function filterTemplatePages(pages, isExperimentEnabled) {
  const templates = [];
  for (const page of pages) {
    const {
      tpaPageId,
      pageSecurity
    } = getAttributesFromPage(page);
    const isProtected = pageSecurity ? pageSecurity.securityType !== SecurityType.NONE : false;
    if (!tpaPageId || isProtected) {
      continue;
    }
    const itemTypeConfig = tpaPageIdToItemTypeConfig[tpaPageId];
    if (!itemTypeConfig) {
      continue;
    }
    const isSupported = staticPageUtils.isSupportedItemTypeTemplatePage(itemTypeConfig.itemType, tpaPageId, isExperimentEnabled);
    if (isSupported) {
      templates.push({
        itemTypeConfig,
        page
      });
    }
  }
  return templates;
}
function buildStaticPageData(page, nonGeneralPageIdPagesGroupMap, i18n, siteStructureGraph) {
  var _page$staticPage, _nonGeneralPageIdPage;
  const {
    id,
    title
  } = page;
  const urlPath = ((_page$staticPage = page.staticPage) == null ? void 0 : _page$staticPage.uri) ?? '';
  const resolvedPath = siteStructureGraph !== undefined && page.id !== undefined && isPageExists(siteStructureGraph, page.id) ? getSlugWithHierarchy(siteStructureGraph, page.id) : urlPath;
  const isMembershipCustomPage = page.managedByWixAppId === MembersCustomPageManagedByWixAppId.SignUp || page.managedByWixAppId === MembersCustomPageManagedByWixAppId.SignIn;
  return {
    dividerTitle: (id && (nonGeneralPageIdPagesGroupMap == null || (_nonGeneralPageIdPage = nonGeneralPageIdPagesGroupMap[id]) == null ? void 0 : _nonGeneralPageIdPage.name)) ?? i18n.t('accessibility-wizard.groups-list.dividers-titles.default'),
    isMembershipCustomPage,
    itemType: ITEM_TYPES.STATIC_PAGE_V2,
    title: title ?? '',
    tpaPage: false,
    urlPath: resolvedPath
  };
}
async function buildTemplatePageData(page, itemTypeConfig, nonGeneralPageIdPagesGroupMap, i18n, httpClient, errorHandler) {
  var _nonGeneralPageIdPage2;
  const {
    id,
    title
  } = page;
  const {
    itemType
  } = itemTypeConfig;
  const innerRoutes = await fetchSiteStructure(httpClient, itemTypeConfig, errorHandler);
  return {
    dividerTitle: (id && (nonGeneralPageIdPagesGroupMap == null || (_nonGeneralPageIdPage2 = nonGeneralPageIdPagesGroupMap[id]) == null ? void 0 : _nonGeneralPageIdPage2.name)) ?? i18n.t('accessibility-wizard.groups-list.dividers-titles.default'),
    innerRoutes,
    itemType,
    title: title ?? '',
    tpaPage: true,
    urlPath: ''
  };
}
const getAttributesFromPage = page => {
  var _page$staticPage2, _page$dynamicPage, _page$staticPage3, _page$dynamicPage2;
  const appDefinitionId = (((_page$staticPage2 = page.staticPage) == null || (_page$staticPage2 = _page$staticPage2.tpa) == null ? void 0 : _page$staticPage2.wixAppId) || ((_page$dynamicPage = page.dynamicPage) == null || (_page$dynamicPage = _page$dynamicPage.tpa) == null ? void 0 : _page$dynamicPage.wixAppId)) ?? undefined;
  const managingAppDefId = page.managedByWixAppId ?? undefined;
  const tpaPageId = (((_page$staticPage3 = page.staticPage) == null || (_page$staticPage3 = _page$staticPage3.tpa) == null ? void 0 : _page$staticPage3.pageIntent) || ((_page$dynamicPage2 = page.dynamicPage) == null || (_page$dynamicPage2 = _page$dynamicPage2.tpa) == null ? void 0 : _page$dynamicPage2.pageIntent)) ?? undefined;
  const pageSecurity = page.security;
  return {
    appDefinitionId,
    managingAppDefId,
    pageSecurity,
    tpaPageId
  };
};
const getPortfolioPagesFromRouters = routersData => {
  var _routersData$find;
  return (_routersData$find = routersData.find(router => router.appId === PORTFOLIO_APP_ID)) == null || (_routersData$find = _routersData$find.config) == null ? void 0 : _routersData$find.portfolioPagesData;
};
const filterOutCMSPages = (pagesDataRaw, routersData) => {
  const cmsPageIds = new Set(routersData.filter(router => router.appId === CMS_APP_ID || router.appId === WIX_CODE_APP_ID).map(router => Object.values(router.pages || {})).flat());
  return pagesDataRaw.filter(page => page.id !== undefined && !cmsPageIds.has(page.id));
};
//# sourceMappingURL=map-page-id-to-page-data-v2.js.map